import React, { useEffect } from 'react';
import { useGetCategoryTreeQuery } from './apiCategoryTree';
import TreeCategory from './TreeCategory';
import { Spin } from 'antd';
import { SectionFixed, SectionHeader, SectionHeaderWithActionsWrapper } from '@ydistri/ds';
import { setOpenedCategory, setSelectedCategory } from './categoryTreeSlice';
import { useAppDispatch } from '../../../store';
import { useStableParams, useStableSearch } from '../../../routes/useStableParams';
import { ROOT_CATEGORY_ID } from '../_constants';
import { CategoryTreePresentationMode } from './CategoryTreeTypes';
import { computeRemSize } from '@ydistri/theme';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';

interface CategoryTreeProps {
  onCategoryChange?: (event: React.MouseEvent, categorySlug: string) => void;
  searchParamModifier?: (value: string) => string;
  presentationMode?: CategoryTreePresentationMode;
}

const CategoryTree: React.FC<CategoryTreeProps> = ({
  onCategoryChange,
  searchParamModifier = (value: string) => value,
  presentationMode = CategoryTreePresentationMode.CONFIGURATION,
}) => {
  const { search } = useStableSearch();
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const { slug } = useStableParams();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (slug) {
      const slugParts = slug.split('-');
      let partialSlug = '';
      slugParts.forEach(slugPart => {
        partialSlug += slugPart;
        dispatch(setOpenedCategory({ categorySlug: partialSlug, opened: true }));
        partialSlug += '-';
      });

      dispatch(setSelectedCategory(slug));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- slug is not a dependency (but why)?
  }, [dispatch]);

  const { data: categories, isFetching } = useGetCategoryTreeQuery(templateOrCalculation.value);

  const modifiedSearch = searchParamModifier(search);

  return (
    <SectionFixed $width={computeRemSize(260)}>
      <SectionHeaderWithActionsWrapper>
        <SectionHeader data-type="Sec">Categories</SectionHeader>
      </SectionHeaderWithActionsWrapper>
      &nbsp;
      <Spin spinning={isFetching}>
        {categories && (
          <TreeCategory
            onCategoryChange={onCategoryChange}
            search={modifiedSearch}
            presentationMode={presentationMode}
            categoryId={ROOT_CATEGORY_ID}
          />
        )}
      </Spin>
    </SectionFixed>
  );
};

export default CategoryTree;
